import type { Client, ClientOptions } from 'graphql-ws'
import { createClient, MessageType } from 'graphql-ws'

import { startConnectionHealthCheck } from './startConnectionHealthCheck'
import { subscribeWithRecovery } from './subscribeWithRecovery'
import { retryDelay, scheduleRetry, wasStable } from './subscriptionRetry'

const RECOVERY_REFRESH_JITTER_MS = 10_000

interface SubscriptionClientOptions {
  url: string
  connectionParams: ClientOptions['connectionParams']
  onReconnect?: () => void
}

interface ConnectionCredentials {
  authorization: unknown
  recoveryPromise?: Promise<boolean>
}

export function createGraphqlSubscriptionClient({
  url,
  connectionParams,
  onReconnect,
}: SubscriptionClientOptions): Pick<Client, 'subscribe'> {
  let activeSubscriptions = 0
  let failedAttempts = 0
  let connectedAt: number | undefined
  let cleanupConnectionHealthCheck: (() => void) | undefined
  let recoveryRefreshTimer: ReturnType<typeof setTimeout> | undefined
  let finishRetry: (() => void) | undefined
  let needsRecoveryRefresh = false
  let connectionCredentials: ConnectionCredentials | undefined

  const getConnectionParams = async () =>
    typeof connectionParams === 'function' ? connectionParams() : connectionParams

  const recoverAuthentication = (): Promise<boolean> => {
    const failedConnection = connectionCredentials
    if (!failedConnection) {
      return Promise.resolve(false)
    }
    failedConnection.recoveryPromise ??= (async () => {
      try {
        const params = await getConnectionParams()
        if (
          activeSubscriptions === 0 ||
          typeof params?.Authorization !== 'string' ||
          !params.Authorization ||
          params.Authorization === failedConnection.authorization
        ) {
          failedConnection.recoveryPromise = undefined
          return false
        }
        // A pending credential lookup must not terminate a replacement connection.
        if (connectionCredentials === failedConnection) {
          client.terminate()
        }
        return true
      } catch {
        failedConnection.recoveryPromise = undefined
        return false
      }
    })()
    return failedConnection.recoveryPromise
  }

  const cancelRecoveryRefresh = () => {
    clearTimeout(recoveryRefreshTimer)
    recoveryRefreshTimer = undefined
  }

  const scheduleRecoveryRefresh = () => {
    if (
      !onReconnect ||
      !needsRecoveryRefresh ||
      connectedAt === undefined ||
      recoveryRefreshTimer !== undefined
    ) {
      return
    }
    // Spread recovery refreshes across clients after an outage.
    recoveryRefreshTimer = setTimeout(() => {
      recoveryRefreshTimer = undefined
      needsRecoveryRefresh = false
      onReconnect()
    }, Math.random() * RECOVERY_REFRESH_JITTER_MS)
  }

  const waitBeforeRetry = () => {
    if (activeSubscriptions === 0) {
      return Promise.resolve()
    }
    return new Promise<void>((resolve) => {
      finishRetry = () => {
        cancel()
        finishRetry = undefined
        resolve()
      }
      const cancel = scheduleRetry(finishRetry, retryDelay(failedAttempts++))
    })
  }

  const client = createClient({
    url,
    connectionParams: async () => {
      const openingConnection: ConnectionCredentials = { authorization: undefined }
      connectionCredentials = openingConnection
      const params = await getConnectionParams()
      openingConnection.authorization = params?.Authorization
      return params
    },
    retryAttempts: Infinity,
    // graphql-ws rejects fatal close codes before calling shouldRetry.
    shouldRetry: () => true,
    retryWait: waitBeforeRetry,
  })

  client.on('opened', (openedSocket) => {
    if (!(openedSocket instanceof WebSocket)) {
      throw new Error('Expected a browser WebSocket')
    }
    cleanupConnectionHealthCheck = startConnectionHealthCheck(client, openedSocket)
  })
  client.on('message', (message) => {
    if (message.type === MessageType.Next && message.payload.data != null) {
      failedAttempts = 0
    }
  })
  client.on('connected', (_socket, _payload, wasRetry) => {
    connectedAt = performance.now()
    if (wasRetry) {
      needsRecoveryRefresh = true
    }
    scheduleRecoveryRefresh()
  })
  const connectionLost = () => {
    if (wasStable(connectedAt)) {
      failedAttempts = 0
    }
    connectedAt = undefined
    cleanupConnectionHealthCheck?.()
    cancelRecoveryRefresh()
  }
  client.on('closed', connectionLost)
  client.on('error', connectionLost)

  const subscribe: Client['subscribe'] = (payload, sink) => {
    activeSubscriptions += 1
    return subscribeWithRecovery(client, payload, sink, {
      onUnauthorized: recoverAuthentication,
      onRestart: () => {
        needsRecoveryRefresh = true
        scheduleRecoveryRefresh()
      },
      onRelease: () => {
        activeSubscriptions -= 1
        if (activeSubscriptions === 0) {
          cancelRecoveryRefresh()
          needsRecoveryRefresh = false
          finishRetry?.()
        }
      },
    })
  }

  return { subscribe }
}
